<template>
  <div
    :class="{ 'border-l-4 border-l-stone-600': hasServerConfigChanges }"
    class="flex flex-col space-y-6 border-t px-12 py-6"
  >
    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
      {{ tScoped("title") }}
    </h3>

    <FormKit
      :actions="false"
      type="form"
      @submit="() => handleSubmitAllSettings()"
      @submit-invalid="globals.focusFirstError"
    >
      <div class="flex flex-col space-y-6">
        <div v-if="showCertSettings" class="min-w-[465px] space-y-4 rounded-lg border p-6">
          <div class="mb-7">
            <h3 class="scroll-m-20 text-xl font-semibold tracking-tight">
              {{ tScoped("certs.title") }}
            </h3>
            <div class="formkit-help text-xs text-neutral-400">
              {{ tScoped("certs.description") }}
            </div>
          </div>

          <div class="flex flex-col gap-6 lg:flex-row lg:items-stretch">
            <!-- Left side: Current Certificate Information -->
            <template v-if="certData && certData.length > 0">
              <div class="flex-1 space-y-3">
                <h4 class="font-semibold">{{ tScoped("certs.currentCert.title") }}</h4>

                <!-- Certificate selector dropdown - only show if multiple certificates -->
                <div v-if="certData.length > 1">
                  <FormKit
                    label="Select Certificate"
                    v-model="selectedCert"
                    :options="
                      certData.map((cert, index) => ({
                        label: getCertificateLabel(index),
                        value: cert,
                      }))
                    "
                    type="select"
                  />
                </div>

                <!-- Selected certificate details -->
                <div v-if="selectedCert" class="space-y-2 text-sm">
                  <div v-if="selectedCert.CommonName">
                    <div class="font-medium text-neutral-400">{{ tScoped("certs.currentCert.commonName") }}</div>
                    <div>
                      {{ selectedCert.CommonName }}
                    </div>
                  </div>

                  <div v-if="selectedCert.AlternativeNames && selectedCert.AlternativeNames.length > 0">
                    <div class="font-medium text-neutral-400">{{ tScoped("certs.currentCert.altNames") }}</div>
                    <div>
                      {{ selectedCert.AlternativeNames.join(", ") }}
                    </div>
                  </div>

                  <div v-if="selectedCert.Expiry">
                    <div class="font-medium text-neutral-400">{{ tScoped("certs.currentCert.expiry") }}</div>
                    <div
                      :class="
                        globals.timeRemaining(selectedCert.Expiry) < 7 * 24 * 60 * 60 * 1000
                          ? 'text-primary'
                          : globals.timeRemaining(selectedCert.Expiry) < 120 * 24 * 60 * 60 * 1000
                            ? 'text-yellow-300'
                            : ''
                      "
                    >
                      {{ globals.formatDate(selectedCert.Expiry) }} ({{ globals.formatAsDays(selectedCert.Expiry) }})
                    </div>
                  </div>
                </div>
              </div>

              <!-- Divider -->
              <div class="hidden self-stretch border-l lg:block"></div>
              <div class="block border-t lg:hidden"></div>
            </template>

            <!-- Right side: Replace Certificate section -->
            <div class="flex flex-1 flex-col">
              <h4 class="font-semibold">{{ tScoped("certs.fileUpload.title") }}</h4>

              <div class="mt-3 flex flex-grow items-center space-x-10">
                <div class="flex w-fit flex-col space-y-2">
                  <FileSelector
                    :label="tScoped('certs.fileUpload.keyFileTitle')"
                    v-model="keyFile"
                    accept=""
                    change-button-text="Replace File"
                    delete-aria-label="Delete key file"
                    no-file-text="No file chosen"
                    select-button-text="Select File"
                  />

                  <FileSelector
                    :label="tScoped('certs.fileUpload.certFileTitle')"
                    v-model="certFile"
                    accept=""
                    change-button-text="Replace File"
                    delete-aria-label="Delete cert file"
                    no-file-text="No file chosen"
                    select-button-text="Select File"
                  />
                </div>

                <div class="flex items-center">
                  <button
                    name="generateNewKeypair"
                    class="mt-6 inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="button"
                    @click="isGenerateNewKeyPairDialogOpen = !isGenerateNewKeyPairDialogOpen"
                  >
                    {{ tScoped("certs.fileUpload.generateNewCerts") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="min-w-[465px] space-y-4 rounded-lg border p-6">
          <div>
            <h3 class="scroll-m-20 text-xl font-semibold tracking-tight">
              {{ tScoped("logging.title") }}
            </h3>
            <div class="formkit-help text-xs text-neutral-400">
              {{ tScoped("logging.description") }}
            </div>
          </div>

          <FormKit
            v-model="currentServerConfigSettings.DebugMode"
            name="DebugMode"
            label="Debug mode (remember to turn off!)"
            type="checkbox"
          />

          <FormKit
            v-model="currentServerConfigSettings.GELFListenerURL"
            :autocomplete="autoComplete"
            :classes="{
              outer: 'max-w-full md:max-w-[50%]',
            }"
            :label="tScoped('logging.label')"
            name="GELFListenerURL"
            placeholder="log.knocknoc.io"
            type="text"
            validation="isGelfUrl"
            :validation-rules="{ isGelfUrl }"
            :validation-messages="{ isGelfUrl: 'URL must be udp, tcp, or tls' }"
          />
        </div>

        <div class="flex justify-end space-x-2">
          <button
            v-if="hasServerConfigChanges"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            type="button"
            @click="handleCancelSettings"
          >
            {{ t("common.cancel") }}
          </button>

          <button
            :disabled="!hasServerConfigChanges"
            :aria-disabled="!hasServerConfigChanges"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            type="submit"
          >
            {{ t("common.save") }}
          </button>
        </div>
      </div>
    </FormKit>

    <ErrorNotification
      :error="errorMessage"
      :title="t('msg.error')"
      @update:error="(input) => (errorMessage = input)"
    />
  </div>

  <Dialog :show="isGenerateNewKeyPairDialogOpen">
    <DialogOverlay />

    <DialogPanel name="GenerateNewKeyPairDialog">
      <DialogTitle>Are you absolutely sure?</DialogTitle>

      <DialogDescription>
        <div class="max-w-lg">
          This action cannot be undone. This will permanently delete your current web server certificate and generate a
          new unsigned one.
          <br />
          <br />
          If your current connection to the server is dependent on the server certificate, you may lose access to the
          web server. Ensure you have another way of accessing the Knocknoc server before proceeding.
        </div>
      </DialogDescription>

      <div class="flex justify-end space-x-2">
        <button
          :disabled="isGeneratingKeyPair"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="isGenerateNewKeyPairDialogOpen = !isGenerateNewKeyPairDialogOpen"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          :disabled="isGeneratingKeyPair"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="handleGenerateKeyPair"
        >
          <ArrowPathIcon v-if="isGeneratingKeyPair" class="animate-spin ease-in-out" />
          {{ t("common.continue") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="showSubmitWithCertChangesDialog">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>Are you absolutely sure?</DialogTitle>

      <DialogDescription>
        <div class="max-w-lg">
          This action cannot be undone. This will replace your current web server certificate, with no way of recovering
          the previous one.
          <br />
          <br />
          If your current connection to the server is dependent on the server certificate, you may lose access to the
          web server. Ensure you have another way of accessing the Knocknoc server before proceeding.
        </div>
      </DialogDescription>

      <div class="flex justify-end space-x-2">
        <button
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="showSubmitWithCertChangesDialog = false"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="
            (s) => {
              showSubmitWithCertChangesDialog = false;
              handleSubmitAllSettings(true);
            }
          "
        >
          {{ t("common.continue") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from "vue";

import { DialogDescription } from "@headlessui/vue";
import { ArrowPathIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

import globals from "../../../globals";
import userService from "../../../services/user.service.ts";
import { useUserStore } from "../../../pinia/user.ts";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../../components/ui/dialog";
import ErrorNotification from "../../../components/ErrorNotification.vue";
import { isEqual } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import InfoNotification from "../../../components/InfoNotification.vue";
import FileSelector from "../Components/FileSelector.vue";

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const tScoped = (key) => {
  const scope = "pages.settings.sections.serverConfiguration";
  return t(`${scope}.${key}`);
};

const userStore = useUserStore();

const { t } = useI18n();

const errorMessage = ref("");
const infoMessage = ref("");

const originalServerConfigSettings = ref(null);
const currentServerConfigSettings = ref(null);

const showCertSettings = ref(null);
const certData = ref(null);
const keyFile = ref(null);
const certFile = ref(null);

watch(keyFile, (val, _) => {
  currentServerConfigSettings.value.Certs.KeyFile = val?.file?.contents || null;
});
watch(certFile, (val, _) => {
  currentServerConfigSettings.value.Certs.CertFile = val?.file?.contents || null;
});

const selectedCert = ref(null);

const getCertificateLabel = (index) => {
  if (index === 0) {
    return `Certificate ${index + 1} (End Entity)`;
  } else if (index === certData.value.length - 1) {
    return `Certificate ${index + 1} (Root CA)`;
  } else {
    return `Certificate ${index + 1} (Intermediate CA)`;
  }
};

const showSubmitWithCertChangesDialog = ref(false);

const hasCertConfigChanges = computed(() => {
  return !isEqual(originalServerConfigSettings.value.Certs, currentServerConfigSettings.value.Certs);
});
const hasServerConfigChanges = computed(() => {
  return !isEqual(originalServerConfigSettings.value, currentServerConfigSettings.value);
});

const isGenerateNewKeyPairDialogOpen = ref(false);

const isGeneratingKeyPair = ref(false);
function handleGenerateKeyPair() {
  isGeneratingKeyPair.value = true;
  userService.generateServerCert().then(
    () => {
      fetchData();
      isGenerateNewKeyPairDialogOpen.value = false;
      isGeneratingKeyPair.value = false;
      infoMessage.value = tScoped("certs.fileUpload.certGenerated");
    },
    (error) => (errorMessage.value = globals.renderError(t, error)),
  );
}

// Function to reset form to original values
function handleCancelSettings() {
  keyFile.value = null;
  certFile.value = null;
  currentServerConfigSettings.value = cloneDeep(originalServerConfigSettings.value);
}

async function handleSubmitAllSettings(proceedWithCertChanges = false) {
  if (!proceedWithCertChanges && hasCertConfigChanges.value) {
    showSubmitWithCertChangesDialog.value = true;
    return;
  }

  await handleSubmitSettings({
    ...originalServerConfigSettings.value,
    ...currentServerConfigSettings.value,
  });
}

async function handleSubmitSettings(s) {
  try {
    await userService.updateServerConfigSettings(s);
    infoMessage.value = t("msg.settingsUpdated");
    fetchData();
  } catch (error) {
    fetchData();
    errorMessage.value = globals.renderError(t, error);
  }
}

async function fetchData() {
  let serverConfig;
  try {
    serverConfig = await userStore.fetchServerConfigSettings({ force: true });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
    return;
  }

  certData.value = serverConfig.data.CertSettings;
  showCertSettings.value = serverConfig.data.ShowCertSettings;
  selectedCert.value = selectedCert.value || certData.value?.[0];

  originalServerConfigSettings.value = getRepostData(serverConfig.data);
  currentServerConfigSettings.value = cloneDeep(originalServerConfigSettings.value);
}

function getRepostData(reqData) {
  return {
    Certs: {
      KeyFile: null,
      CertFile: null,
    },
    DebugMode: reqData?.DebugMode,
    GELFListenerURL: reqData?.GELFListenerURL,
  };
}

function isGelfUrl({ value }) {
  try {
    const url = new URL(value);
    return url.protocol === "udp:" || url.protocol === "tcp:" || url.protocol === "tls:";
  } catch {
    return false;
  }
}

onBeforeMount(() => {
  originalServerConfigSettings.value = getRepostData(undefined);
  currentServerConfigSettings.value = getRepostData(undefined);
});

onMounted(() => {
  fetchData();
});
</script>
